/* ── Modal.helpers ──────────────────────────────────────────────
 * Everything the dialog needs to keep the Tab key inside itself.
 *
 * WHY this file exists
 * --------------------
 * Bootstrap ships a focus trap (`enforceFocus`) that watches focus and
 * yanks it back the instant it lands outside the dialog. Modal.js
 * switches that trap OFF — see the comment there — because our
 * dropdowns (Select, SearchableSelect, MultiSelect) render their option
 * list through a React *portal* into <body>, i.e. into DOM that is not
 * inside the dialog at all. Bootstrap's trap sees the search box in
 * that popup as "outside", steals focus back, and the field cannot be
 * typed in.
 *
 * The price of switching the trap off was that nothing then stopped the
 * Tab key walking out of the last button in the dialog and into the
 * page behind the backdrop — the sidebar, links, form fields — none of
 * which a mouse can reach, because the backdrop covers them. A
 * keyboard-only user had no way back in except Shift+Tab, counting.
 *
 * So rather than a trap that fights the portals, this file builds the
 * dialog's own Tab ring: every focusable element inside the dialog,
 * plus the focusables of any popup the dialog's own comboboxes have
 * portaled out, in the order a person expects to meet them (a popup
 * sits immediately after the field that opened it). Modal.js then only
 * steps in at the two ends of that ring, and leaves every Tab in the
 * middle to the browser.
 *
 * `nextFocusIndex` at the bottom is pure index arithmetic and is unit
 * tested; the rest reads the DOM and never writes to it — Modal.js owns
 * the one `.focus()` call.
 * ────────────────────────────────────────────────────────────── */

/* ── What counts as focusable ────────────────────────────────── */

// The elements a browser will stop on when Tab is pressed. `[tabindex]`
// catches custom widgets that opted in — including our own dropdown
// triggers, which are <div role="combobox" tabIndex={0}>.
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'iframe',
  'audio[controls]',
  'video[controls]',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',');

const isFocusable = (el) => {
  // tabindex="-1" is the author saying "reachable by script, skipped by
  // Tab" — the dialog's own outer <div role="dialog" tabindex="-1"> is
  // the obvious one. The ATTRIBUTE is read rather than the `tabIndex`
  // property on purpose: browsers report -1 from the property for
  // several elements that are perfectly tabbable, an <iframe> among
  // them (the PDF viewer dialog in File Management has one), and
  // dropping those from the ring would make them unreachable.
  const tabindex = el.getAttribute('tabindex');
  if (tabindex !== null && Number(tabindex) < 0) return false;
  if (el.disabled) return false;
  // aria-hidden / hidden on the element OR on anything it sits inside:
  // a collapsed section's buttons are still in the DOM but must not be
  // part of the ring.
  if (el.closest('[hidden]') || el.closest('[aria-hidden="true"]')) return false;
  // Only the element's OWN computed style is consulted. The geometric
  // checks a real browser allows (offsetParent, getClientRects) report
  // "invisible" for absolutely everything in jsdom, which has no layout
  // engine — using them would empty the ring in every test while
  // looking fine in the browser.
  const style = typeof window !== 'undefined' ? window.getComputedStyle(el) : null;
  if (style && (style.display === 'none' || style.visibility === 'hidden')) return false;
  return true;
};

export const collectFocusable = (root) =>
  root ? Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isFocusable) : [];

/* ── Popups that live outside the dialog ─────────────────────── */

/**
 * The portaled popup a control inside the dialog has opened, or null.
 *
 * The link is `aria-controls`, which every one of our comboboxes
 * already sets to the id of its option list — it is there for screen
 * readers, and it happens to be exactly the "this popup is mine"
 * statement we need. We then walk out from the list to the node the
 * portal actually mounted at <body>, because the list is only part of
 * the popup: SearchableSelect's search box is its sibling, and the
 * search box is the focusable one.
 */
export const portaledPopupOf = (trigger, dialog) => {
  const controls = trigger.getAttribute && trigger.getAttribute('aria-controls');
  if (!controls || !dialog) return null;
  const controlled = document.getElementById(controls);
  // Inside the dialog already: nothing special to do, it is picked up
  // by the ordinary sweep.
  if (!controlled || dialog.contains(controlled)) return null;
  let node = controlled;
  while (node.parentElement && node.parentElement !== document.body) {
    node = node.parentElement;
  }
  // Guard against an id that resolves to something wrapping the dialog
  // (a page-level container): that is not a popup, and treating it as
  // one would pull the whole page into the ring.
  if (node.contains(dialog)) return null;
  return node;
};

/**
 * The dialog's Tab ring, in order, plus the set of members that are
 * NOT in the dialog's DOM.
 *
 * That second value matters: the browser moves Tab along DOM order, and
 * a portaled popup sits at the very end of <body>, nowhere near the
 * field that opened it. So for those elements the browser's idea of
 * "next" is wrong and Modal.js has to drive the move itself — while for
 * everything else our order and the browser's are the same, and the
 * browser can be left alone.
 */
export const dialogFocusOrder = (dialog) => {
  const order = [];
  const portaled = new Set();
  collectFocusable(dialog).forEach((el) => {
    order.push(el);
    if (el.getAttribute('aria-expanded') !== 'true') return;
    const popup = portaledPopupOf(el, dialog);
    if (!popup) return;
    collectFocusable(popup).forEach((inner) => {
      order.push(inner);
      portaled.add(inner);
    });
  });
  return { order, portaled };
};

/**
 * Is this the dialog on top? Two dialogs can be open at once (a confirm
 * over an editor). Both run the Tab handler, and both would otherwise
 * try to reel in focus that is sitting outside either of them — the
 * lower one winning purely by listener order. Whichever `.modal`
 * appears last in the document is the one drawn on top, because
 * Bootstrap appends each new dialog to <body>.
 */
export const isTopDialog = (dialog) => {
  const dialogs = Array.from(document.querySelectorAll('.modal[role="dialog"]'));
  return dialogs.length === 0 || dialogs[dialogs.length - 1] === dialog;
};

/* ── The pure part ───────────────────────────────────────────── */

/**
 * Where Tab should land, or `null` for "leave the browser to it".
 *
 *   count     how many elements are in the ring
 *   current   index of the focused element, -1 when focus is not in the
 *             ring at all (it has wandered out, or it is sitting on the
 *             dialog's own tabindex="-1" container right after opening)
 *   shiftKey  true for Shift+Tab, which walks the ring backwards
 *   force     true when the browser's own idea of "next" cannot be
 *             trusted — i.e. focus is inside a popup portaled to the end
 *             of <body>, from where the browser would step out of the
 *             page entirely instead of back into the dialog
 *
 * Returning null for the ordinary middle-of-the-ring case is the whole
 * point: the browser's native Tab already honours DOM order, hidden
 * fields, disabled buttons and everything else it knows about our
 * markup. We only correct it where it is wrong.
 */
export const nextFocusIndex = ({ count, current, shiftKey = false, force = false }) => {
  if (!count || count <= 0) return null;
  const last = count - 1;
  if (current < 0 || current > last) return shiftKey ? last : 0;
  if (!shiftKey && current === last) return 0;
  if (shiftKey && current === 0) return last;
  return force ? current + (shiftKey ? -1 : 1) : null;
};
